import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock, FileImage, FileVideo, FileAudio, FileText, FileQuestion,
  ChevronRight, Trash2, ScanLine, Loader2,
} from "lucide-react";
import { api } from "../api/client";
import { toast } from "sonner";

const verdictMap = {
  safe: { label: "Seguro", color: "#8BF56C" },
  suspicious: { label: "Sospechoso", color: "#F4D03F" },
  manipulated: { label: "Manipulado", color: "#FF5A6E" },
};

const mediaIcon = (type) => {
  if (type === "image") return FileImage;
  if (type === "video") return FileVideo;
  if (type === "audio") return FileAudio;
  if (type === "document") return FileText;
  return FileQuestion;
};

export default function History() {
  const navigate = useNavigate();
  const [scans, setScans] = useState(null);

  const load = () => api.get("/scans").then(({ data }) => setScans(data));

  useEffect(() => { load(); }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/scans/${id}`);
      setScans((s) => s.filter((x) => x.id !== id));
      toast.success("Registro eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Grid */}
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.016) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.016) 1px,transparent 1px)', backgroundSize: '72px 72px' }} />
      {/* Ambient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="orb-float absolute rounded-full" style={{ width: 500, height: 500, top: -160, right: -120, background: 'radial-gradient(circle, rgba(222,255,154,0.05) 0%, transparent 65%)' }} />
        <div className="orb-float-2 absolute rounded-full" style={{ width: 420, height: 420, bottom: -120, left: -80, background: 'radial-gradient(circle, rgba(0,87,255,0.045) 0%, transparent 65%)' }} />
      </div>
    <div className="relative z-10 pb-24 px-5 sm:px-8 pt-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <Clock size={18} className="text-[#DEFF9A]" />
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#DEFF9A]">
          Cadena de custodia
        </span>
      </div>
      <h1 className="font-display text-4xl tracking-tighter mb-2">Historial</h1>
      <p className="text-[#8A8F98] text-sm mb-8">
        Registro inmutable de tus análisis forenses previos.
      </p>

      {scans === null && (
        <div className="flex flex-col items-center gap-4 py-20">
          <div className="relative flex items-center justify-center" style={{ width: 64, height: 64 }}>
            <div className="scan-ring absolute rounded-full" style={{ width: 36, height: 36, border: '1px solid rgba(222,255,154,0.35)' }} />
            <div className="scan-ring-2 absolute rounded-full" style={{ width: 36, height: 36, border: '1px solid rgba(222,255,154,0.2)' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={18} className="text-[#DEFF9A] animate-spin" />
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/25">Cargando registros...</div>
        </div>
      )}

      {scans && scans.length === 0 && (
        <div data-testid="history-empty" className="relative bg-[#0A0A0A] border border-[#1F2428] rounded-2xl py-14 px-6 overflow-hidden">
          <div className="absolute inset-0 scan-grid opacity-25 pointer-events-none" />
          <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[#DEFF9A]/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col items-center text-center">
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-full bg-[#DEFF9A]/15 blur-xl" />
              <div className="relative w-16 h-16 rounded-2xl bg-[#DEFF9A]/10 border border-[#DEFF9A]/30 flex items-center justify-center">
                <Clock size={26} className="text-[#DEFF9A]" />
              </div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#5A5F68] mb-3">
              Registro vacío
            </span>
            <h3 className="font-display text-2xl tracking-tighter mb-2">
              Aún no hay análisis
            </h3>
            <p className="text-[#8A8F98] text-sm max-w-xs mb-6">
              Tu primer análisis aparecerá aquí, encriptado y solo accesible para ti.
            </p>
            <button
              data-testid="empty-cta-scan"
              onClick={() => navigate("/app")}
              className="inline-flex items-center gap-2 bg-[#DEFF9A] hover:bg-[#C8FF6E] text-black font-bold rounded-lg px-5 py-2.5 transition-colors text-sm glow-acid"
            >
              <ScanLine size={16} />
              Iniciar primer análisis
            </button>
            <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.25em] text-[#5A5F68]">
              Cifrado de extremo a extremo · Tus datos · Tu control
            </p>
          </div>
        </div>
      )}

      {scans && scans.length > 0 && (
        <ul className="space-y-3" data-testid="history-list">
          {scans.map((s) => {
            const Icon = mediaIcon(s.media_type);
            const v = verdictMap[s.verdict] || verdictMap.safe;
            const ts = new Date(s.created_at);
            return (
              <li
                key={s.id}
                data-testid={`history-item-${s.id}`}
                onClick={() => navigate(`/app/resultado/${s.id}`)}
                className="bg-white/[0.025] border border-white/[0.06] hover:border-white/[0.18] hover:bg-white/[0.04] rounded-xl p-4 flex items-center gap-4 cursor-pointer transition-all duration-200 group"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}
              >
                <div className="w-11 h-11 rounded-lg bg-[#141414] flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-[#8A8F98]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.color, boxShadow: `0 0 6px ${v.color}` }} />
                    <span className="text-xs font-semibold" style={{ color: v.color }}>{v.label}</span>
                    <span className={`text-[9px] font-mono uppercase tracking-[0.18em] rounded-full px-2 py-0.5 border ${
                      s.analysis_mode === "verify"
                        ? "text-[#9AD7FF] border-[#9AD7FF]/30 bg-[#9AD7FF]/10"
                        : "text-[#DEFF9A] border-[#DEFF9A]/30 bg-[#DEFF9A]/10"
                    }`}>
                      {s.analysis_mode === "verify" ? `Verify · ${s.sector || "general"}` : "Legal"}
                    </span>
                    <span className="font-mono text-xs font-bold ml-auto" style={{ color: v.color }}>
                      {100 - s.truthfulness_score}%
                    </span>
                  </div>
                  <div className="text-sm font-medium truncate">{s.original_filename}</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#5A5F68] mt-0.5">
                    {ts.toLocaleString("es-ES")}
                  </div>
                </div>
                <button
                  data-testid={`history-delete-${s.id}`}
                  onClick={(e) => remove(e, s.id)}
                  className="text-[#5A5F68] hover:text-[#FF5A6E] transition-colors p-2"
                >
                  <Trash2 size={16} />
                </button>
                <ChevronRight size={16} className="text-[#5A5F68] group-hover:text-[#DEFF9A] transition-colors" />
              </li>
            );
          })}
        </ul>
      )}
    </div>
    </div>
  );
}

